import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  QrCodeIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import {
  conferenciaApi,
  type ConferenciaDetalhes,
  type NfeConferencia,
  type ConferenciaProduto
} from '../api/conferencia';
import { formatDateTime } from '../utils/format';
import { useToast } from '../components/ui/Toast';
import { logError } from '../utils/clientLog';

const ConferenciaDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { empresa, codigoId } = useParams<{ empresa: string; codigoId: string }>();
  const [conferencia, setConferencia] = useState<ConferenciaDetalhes | null>(null);
  const [nfeSelecionada, setNfeSelecionada] = useState<NfeConferencia | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Estados para o scanner de código de barras
  const [codigoBarras, setCodigoBarras] = useState('');
  const [scannerAtivo, setScannerAtivo] = useState(false);
  const codigoBarrasRef = useRef<HTMLInputElement>(null);

  // Estados para busca de NFe
  const [danfeInput, setDanfeInput] = useState('');
  const [buscandoNfe, setBuscandoNfe] = useState(false);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Carregar conferência
  useEffect(() => {
    if (!empresa || !codigoId) return;

    const carregarConferencia = async () => {
      try {
        setLoading(true);
        const dados = await conferenciaApi.obterConferencia(empresa, codigoId);
        setConferencia(dados);
      } catch (err) {
        logError('Erro ao carregar conferência:', err);
        setError('Erro ao carregar conferência');
      } finally {
        setLoading(false);
      }
    };

    carregarConferencia();
  }, [empresa, codigoId]);

  // Auto-focus no scanner quando ativado
  useEffect(() => {
    if (scannerAtivo && codigoBarrasRef.current) {
      codigoBarrasRef.current.focus();
    }
  }, [scannerAtivo]);

  // Buscar NFe por DANFE
  const buscarNfePorDanfe = async () => {
    if (!danfeInput || danfeInput.length !== 44) {
      toast.aviso('DANFE deve ter 44 dígitos');
      return;
    }

    try {
      setBuscandoNfe(true);
      const nfe = await conferenciaApi.buscarNfePorDanfe(danfeInput);
      setNfeSelecionada(nfe);
    } catch (err) {
      logError('Erro ao buscar NFe:', err);
      toast.erro('NFe não encontrada ou não aprovada');
    } finally {
      setBuscandoNfe(false);
    }
  };

  // Processar código de barras
  const processarCodigoBarras = async () => {
    if (!codigoBarras.trim()) return;

    try {
      const produto = await conferenciaApi.buscarProdutoPorBarras(codigoBarras);
      
      if (!conferencia) return;

      // Verificar se produto já existe na conferência
      const produtoExistente = conferencia.produtos.find(p => p.produto === produto.produtoPrincipal);
      
      if (produtoExistente) {
        // Incrementar quantidade
        const novosProds = conferencia.produtos.map(p => 
          p.produto === produto.produtoPrincipal 
            ? { ...p, qtdeConferida: (p.qtdeConferida || 0) + 1 }
            : p
        );
        setConferencia({ ...conferencia, produtos: novosProds });
      } else {
        // Adicionar novo produto
        const novoProduto: ConferenciaProduto = {
          produto: produto.produtoPrincipal,
          qtdeConferida: 1,
          qtdeEmbalagem: 1,
          status: 'CONFERIDO',
          descricaoProduto: produto.descricao || 'Produto não identificado',
        };
        setConferencia({ 
          ...conferencia, 
          produtos: [...conferencia.produtos, novoProduto] 
        });
      }

      // Limpar campo e manter foco
      setCodigoBarras('');
      setTimeout(() => {
        codigoBarrasRef.current?.focus();
      }, 100);

    } catch (err) {
      logError('Erro ao buscar produto:', err);
      toast.erro('Produto não encontrado com este código de barras');
      setCodigoBarras('');
    }
  };

  // Salvar alterações
  const salvarAlteracoes = async () => {
    if (!conferencia || !empresa || !codigoId) return;

    try {
      await conferenciaApi.atualizarConferencia(empresa, codigoId, {
        observacao: conferencia.observacao,
        status: conferencia.status,
        produtos: conferencia.produtos.map(p => ({
          produto: p.produto,
          qtdeConferida: p.qtdeConferida,
          qtdeEmbalagem: p.qtdeEmbalagem,
          status: p.status || 'PENDENTE',
        })),
      });

      setEditMode(false);
      toast.sucesso('Conferência salva com sucesso!');
    } catch (err) {
      logError('Erro ao salvar conferência:', err);
      toast.erro('Erro ao salvar conferência');
    }
  };

  // Atualizar quantidade do produto
  const atualizarQuantidadeProduto = (produtoId: string, novaQtde: number) => {
    if (!conferencia) return;

    const novosProds = conferencia.produtos.map(p =>
      p.produto === produtoId 
        ? { ...p, qtdeConferida: Math.max(0, novaQtde) }
        : p
    );
    setConferencia({ ...conferencia, produtos: novosProds });
  };

  // Remover produto
  const removerProduto = (produtoId: string) => {
    if (!conferencia) return;

    const novosProds = conferencia.produtos.filter(p => p.produto !== produtoId);
    setConferencia({ ...conferencia, produtos: novosProds });
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONCLUIDO':
        return 'bg-emerald-100 text-emerald-800';
      case 'EM_ANDAMENTO':
        return 'bg-amber-100 text-amber-800';
      case 'CANCELADO':
        return 'bg-rose-100 text-rose-800';
      case 'CONFERIDO':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-zinc-100 text-zinc-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !conferencia) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <p className="text-rose-600 mb-4">{error || 'Conferência não encontrada'}</p>
          <button
            onClick={() => navigate('/conferencia')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            Voltar para lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header canônico (UX_AUDIT R1) */}
        <button
          onClick={() => navigate('/conferencia')}
          className="mb-3 flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5 mr-1" />
          Voltar para lista
        </button>
        <header className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-2">
            {isMobile
              ? <DevicePhoneMobileIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
              : <ClipboardDocumentListIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />}
            <div>
              <h1 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Conferência {conferencia.codigoId}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                <span>Empresa: {conferencia.empresa}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(conferencia.status)}`}>
                  {conferencia.status || 'Indefinido'}
                </span>
                <span>Data: {formatDateTime(conferencia.dataConferencia!)}</span>
                {conferencia.usuario && <span>Usuário: {conferencia.usuario}</span>}
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            {editMode ? (
              <>
                <button
                  onClick={salvarAlteracoes}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 flex items-center"
                >
                  <CheckIcon className="h-5 w-5 mr-2" />
                  Salvar
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="bg-zinc-600 text-white px-4 py-2 rounded-md hover:bg-zinc-700 flex items-center"
                >
                  <XMarkIcon className="h-5 w-5 mr-2" />
                  Cancelar
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditMode(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Editar Conferência
              </button>
            )}
          </div>
        </header>

        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 lg:grid-cols-2 gap-6'}`}>
          {/* Painel Esquerdo - Busca NFe e Scanner */}
          <div className="space-y-6">
            {/* Buscar NFe por DANFE */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Buscar NFe</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Número DANFE (44 dígitos)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={danfeInput}
                      onChange={(e) => setDanfeInput(e.target.value)}
                      placeholder="Digite o código DANFE..."
                      maxLength={44}
                      className="flex-1 border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      onClick={buscarNfePorDanfe}
                      disabled={buscandoNfe || danfeInput.length !== 44}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                    >
                      {buscandoNfe ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                          Buscar
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    {danfeInput.length}/44 dígitos
                  </p>
                </div>

                {nfeSelecionada && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h3 className="font-medium text-blue-900">NFe Encontrada</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Número: {nfeSelecionada.numeroNfe}</p>
                      <p>Fornecedor: {nfeSelecionada.fornecedor}</p>
                      <p>Data: {formatDateTime(nfeSelecionada.dataEmissao!)}</p>
                      <p>Valor: R$ {nfeSelecionada.valorTotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Produtos: {nfeSelecionada.produtos.length}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Scanner de Código de Barras */}
            {editMode && (
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-zinc-900">Scanner de Código de Barras</h2>
                  <button
                    onClick={() => setScannerAtivo(!scannerAtivo)}
                    className={`px-4 py-2 rounded-md flex items-center ${
                      scannerAtivo 
                        ? 'bg-rose-600 text-white hover:bg-rose-700' 
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    <QrCodeIcon className="h-5 w-5 mr-2" />
                    {scannerAtivo ? 'Desativar' : 'Ativar'} Scanner
                  </button>
                </div>

                {scannerAtivo && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                      <p className="text-amber-800 text-sm">
                        💡 Scanner ativo! Use o leitor de código de barras ou digite manualmente.
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <input
                        ref={codigoBarrasRef}
                        type="text"
                        value={codigoBarras}
                        onChange={(e) => setCodigoBarras(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            processarCodigoBarras();
                          }
                        }}
                        placeholder="Escaneie ou digite o código de barras..."
                        className="flex-1 border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        onClick={processarCodigoBarras}
                        disabled={!codigoBarras.trim()}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Observações */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Observações</h2>
              {editMode ? (
                <textarea
                  value={conferencia.observacao || ''}
                  onChange={(e) => setConferencia({ ...conferencia, observacao: e.target.value })}
                  placeholder="Digite observações sobre a conferência..."
                  rows={4}
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              ) : (
                <p className="text-zinc-700">
                  {conferencia.observacao || 'Nenhuma observação registrada'}
                </p>
              )}
            </div>
          </div>

          {/* Painel Direito - Produtos */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4">
              Produtos ({conferencia.produtos.length})
            </h2>

            {conferencia.produtos.length === 0 ? (
              <div className="text-center py-12">
                <QrCodeIcon className="h-12 w-12 text-zinc-400 mx-auto mb-4" />
                <p className="text-zinc-500">
                  Nenhum produto conferido ainda.
                </p>
                {editMode && (
                  <p className="text-sm text-zinc-400 mt-2">
                    Use o scanner de código de barras para adicionar produtos.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {conferencia.produtos.map((produto) => (
                  <div
                    key={produto.produto}
                    className="border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-zinc-900 truncate">
                          {produto.descricaoProduto}
                        </h3>
                        <p className="text-sm text-zinc-500 mt-1">
                          Código: {produto.produto}
                        </p>
                        <div className="mt-2 flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-zinc-600">Qtde:</span>
                            {editMode ? (
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => atualizarQuantidadeProduto(produto.produto, (produto.qtdeConferida || 0) - 1)}
                                  className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded-md text-zinc-600 hover:bg-zinc-50"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                                <input
                                  type="number"
                                  value={produto.qtdeConferida || 0}
                                  onChange={(e) => atualizarQuantidadeProduto(produto.produto, parseFloat(e.target.value) || 0)}
                                  className="w-16 text-center border border-zinc-300 rounded-md py-1"
                                  min="0"
                                  step="0.01"
                                />
                                <button
                                  onClick={() => atualizarQuantidadeProduto(produto.produto, (produto.qtdeConferida || 0) + 1)}
                                  className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded-md text-zinc-600 hover:bg-zinc-50"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <span className="font-medium">{produto.qtdeConferida || 0}</span>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(produto.status)}`}>
                            {produto.status || 'Pendente'}
                          </span>
                        </div>
                      </div>
                      {editMode && (
                        <button
                          onClick={() => removerProduto(produto.produto)}
                          className="ml-4 p-2 text-rose-600 hover:bg-rose-50 rounded-md"
                          title="Remover produto"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConferenciaDetailPage;